/**
 * OPENYC AI Avatar Chat Application
 * Complete implementation based on yc.rtf specification
 * API: OpenClaw/Molobot backend
 */

import React, { useState, useEffect, useCallback } from 'react';

import openclawService from './services/openclawService';
import UserGroupChat from './components/UserGroupChat';
import PrivateChat from './components/PrivateChat';
import StrangerChatModal from './components/StrangerChatModal';
import ChatLogSheet from './components/ChatLogSheet';
import Moments from './components/Moments';
import ChatList from './components/ChatList';
import FriendPickerModal from './components/FriendPickerModal';
import {
  getCurrentUser,
  onAuthStateChange
} from './services/firebaseAuth';
import api from './api';
import { TRANSLATIONS } from './constants/translations';

import GlobalStyles from './components/ui/GlobalStyles';
import StatusBar from './components/ui/StatusBar';
import ChatHeader from './components/chat/ChatHeader';
import MessageList from './components/chat/MessageList';
import MessageInput from './components/chat/MessageInput';
import SidebarMenu from './components/SidebarMenu';
import QRCodeModal from './components/modals/QRCodeModal';
import SubscriptionModal from './components/modals/SubscriptionModal';
import GroupListModal from './components/GroupListModal';
import LoginScreen from './components/views/LoginScreen';
import { triggerHaptic } from './utils/haptics';

// Initialize Firebase via firebaseAuth service
const { getAuthInstance, getAppInstance } = require('./services/firebaseAuth');

try {
  getAppInstance();
  getAuthInstance();
  console.log('Firebase initialized via service');
} catch (e) {
  console.warn('Firebase initialization skipped:', e);
}

/* --- Main App Component --- */
const App = () => {
  // Core auth & user state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [userName, setUserName] = useState('Avatar');
  const [tempUserName, setTempUserName] = useState('');
  const [isEditingUserName, setIsEditingUserName] = useState(false);
  const [avatarImage, setAvatarImage] = useState('https://api.dicebear.com/7.x/bottts/svg?seed=OPENYC');

  // Settings & AI state
  const [settings, setSettings] = useState({ language: 'zh', theme: 'dark', notifications: true });
  const [aiAssistantName, setAiAssistantName] = useState('AI Assistant');
  const [agentConfig, setAgentConfig] = useState({
    browser_control: { enabled: true },
    calendar_control: { enabled: true },
    file_management: { enabled: true }
  });
  const [availableAgents, setAvailableAgents] = useState([]);

  // UI state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [isGroupsOpen, setIsGroupsOpen] = useState(false);
  const [isSubscriptionOpen, setIsSubscriptionOpen] = useState(false);
  const [isMomentsOpen, setIsMomentsOpen] = useState(false);
  const [isChatListOpen, setIsChatListOpen] = useState(false);
  const [showFriendPicker, setShowFriendPicker] = useState(false);
  const [friends, setFriends] = useState([]);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [msgCount, setMsgCount] = useState(0);
  const [friendRequest, setFriendRequest] = useState(null);
  const [groupJoinRequest, setGroupJoinRequest] = useState(null);

  // Stranger chat state
  const [showStrangerChat, setShowStrangerChat] = useState(false);
  const [strangerChatData, setStrangerChatData] = useState(null);

  const [activeUserGroup, setActiveUserGroup] = useState(null);
  const [activePrivateChat, setActivePrivateChat] = useState(null);

  // Chat log sheet state
  const [chatLogSheet, setChatLogSheet] = useState({
    isOpen: false,
    chatLog: [],
    strangerName: '',
    status: 'pending'
  });

  const t = TRANSLATIONS[settings.language] || TRANSLATIONS.en;

  const loadChatHistory = useCallback(async () => {
    if (!isLoggedIn || !user?.uid) return;
    
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (token && api && api.axios) {
      api.axios.defaults.headers['Authorization'] = `Bearer ${token}`;
    }
    
    try {
      const history = await api.getMessages(user.uid, 50);
      if (history && history.length > 0) {
        const formattedMessages = history.map(msg => ({
          id: msg.id,
          text: msg.content,
          sender: msg.isAI ? 'ai' : 'me',
          isAI: msg.isAI,
          timestamp: new Date(msg.createdAt),
          metadata: msg.metadata
        }));
        setMessages(formattedMessages);
        setMsgCount(formattedMessages.length);
      }
    } catch (err) {
      console.warn('[App] Failed to load chat history:', err.message);
    }
  }, [isLoggedIn, user?.uid]);

  const loadFriends = useCallback(async () => {
    if (!user?.uid) return;
    try {
      const friendList = await api.getFriends(user.uid);
      setFriends(friendList || []);
    } catch (err) {
      console.error('加载好友列表失败:', err);
    }
  }, [user?.uid]);

  useEffect(() => {
    if (isLoggedIn) {
      loadChatHistory();
      loadFriends();
    }
  }, [isLoggedIn, loadChatHistory, loadFriends]);

  useEffect(() => {
    if (!isLoggedIn || !user?.uid || messages.length > 0) return;

    const todayKey = `ai_greeting_date_${user.uid}`;
    const today = new Date().toDateString();
    if (localStorage.getItem(todayKey) === today) return;

    const greetings = [
      '今天怎么样，和我聊聊吗？',
      '早上好！今天有什么新鲜事吗？',
      '嗨！有什么想分享的吗？',
      '今天心情如何？来聊聊天吧！',
      '新的一天开始了，有什么计划吗？'
    ];

    const timer = setTimeout(async () => {
      try {
        const text = greetings[Math.floor(Math.random() * greetings.length)];
        const aiMsg = { id: Date.now(), text, sender: 'ai', isAI: true, timestamp: new Date() };
        setMessages(prev => [...prev, aiMsg]);
        localStorage.setItem(todayKey, today);
        await api.sendMessage(user.uid, user.uid, aiAssistantName || 'AI助手', text, true);
      } catch (err) {
        console.warn('[App] AI greeting failed:', err.message);
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [isLoggedIn, user?.uid, messages.length, aiAssistantName]);

  useEffect(() => {
    if (!isLoggedIn) return;
    openclawService.ensureInstance().then(instance => {
      if (instance?.name) setAiAssistantName(instance.name);
    });
  }, [isLoggedIn]);

  // Load saved user name from localStorage
  useEffect(() => {
    const savedUserName = localStorage.getItem('user_display_name');
    if (savedUserName) {
      setUserName(savedUserName);
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !user?.uid) return;

    api.joinUserRoom(user.uid);

    api.getNotifications(user.uid, true).then(notifications => {
      console.log('[App] Unread notifications:', notifications.length);
      const notifMsgs = [];
      notifications.forEach(notif => {
        if (notif.type === 'friend_request') {
          const data = notif.data;
          notifMsgs.push({
            id: notif.id,
            sender: 'system',
            text: `${data.request.userId} 想添加你为好友`,
            timestamp: new Date(notif.createdAt),
            isFriendRequest: true,
            requestData: {
              strangerId: data.request.userId,
              strangerName: data.request.userId,
              status: 'pending'
            }
          });
        } else if (notif.type === 'system_message') {
          notifMsgs.push({
            id: notif.id,
            sender: 'system',
            text: notif.data.content,
            timestamp: new Date(notif.createdAt),
            isSystem: true
          });
        }
      });
      
      if (notifMsgs.length > 0) {
        setMessages(prev => {
          const allMsgs = [...prev, ...notifMsgs];
          return allMsgs.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        });
      }
    });

    const handleFriendRequest = (data) => {
      if (data.type === 'new_request') {
        const requestMsg = {
          id: Date.now(),
          sender: 'system',
          text: `${data.request.userId} 想添加你为好友`,
          timestamp: new Date(),
          isFriendRequest: true,
          requestData: {
            strangerId: data.request.userId,
            strangerName: data.request.userId,
            status: 'pending'
          }
        };
        setMessages(prev => {
          const newMsgs = [...prev, requestMsg];
          return newMsgs.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        });
      }
    };

    const handleSystemMessage = (data) => {
      console.log('[App] Received system message:', data);
      const systemMsg = {
        id: Date.now(),
        sender: 'system',
        text: data.content,
        timestamp: new Date(),
        isSystem: true
      };
      console.log('[App] Adding system message to chat:', systemMsg);
      setMessages(prev => {
        const newMsgs = [...prev, systemMsg];
        return newMsgs.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      });
    };

    const handleMessage = (data) => {
      console.log('[App] Received message:', data);
      if (data.isAI) {
        setMessages(prev => {
          const exists = prev.some(m => m.id === data.id || (m.text === data.content && m.isAI));
          if (exists) {
            console.log('[App] AI message already exists, skipping');
            return prev;
          }
          const aiMsg = {
            id: data.id || Date.now(),
            sender: data.senderName || 'AI分身',
            text: data.content,
            timestamp: new Date(data.createdAt || Date.now()),
            isAI: true,
            senderId: data.senderId
          };
          console.log('[App] Adding AI message to chat:', aiMsg);
          const newMessages = [...prev, aiMsg];
          return newMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        });
      }
    };

    api.onFriendRequest(handleFriendRequest);
    api.onSystemMessage(handleSystemMessage);
    api.onNewMessage(handleMessage);

    return () => {
      if (api.socket) {
        api.socket.off('friend_request', handleFriendRequest);
        api.socket.off('system_message', handleSystemMessage);
        api.socket.off('message', handleMessage);
      }
    };
  }, [isLoggedIn, user?.uid]);

  // Load agents
  useEffect(() => {
    if (!isLoggedIn) return;

    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (token && api && api.axios) {
      api.axios.defaults.headers['Authorization'] = `Bearer ${token}`;
    }
    
    api.getAgents().then(data => {
      console.log('[App] Agents response:', data);
      if (data.success && data.agents) {
        console.log('[App] Setting available agents:', data.agents);
        setAvailableAgents(data.agents);
      } else {
        console.warn('[App] No agents in response, using defaults');
        // Use default agents if backend doesn't return any
        setAvailableAgents([
          {
            id: 'browser_agent',
            name: 'Browser Control',
            description: 'Can control browser, navigate websites, interact with pages',
            capabilities: ['navigate', 'click', 'fill_form', 'screenshot', 'scroll'],
            enabled: true
          },
          {
            id: 'calendar_agent',
            name: 'Calendar Control',
            description: 'Can read and manage calendar events',
            capabilities: ['read_events', 'create_event', 'update_event', 'delete_event'],
            enabled: true
          },
          {
            id: 'file_agent',
            name: 'File Management',
            description: 'Can manage files and folders',
            capabilities: ['list_files', 'upload_file', 'download_file', 'delete_file'],
            enabled: true
          }
        ]);
      }
    }).catch(error => {
      console.error('Failed to load agents:', error);
      // Use default agents on error
      setAvailableAgents([
        {
          id: 'browser_agent',
          name: 'Browser Control',
          description: 'Can control browser, navigate websites, interact with pages',
          capabilities: ['navigate', 'click', 'fill_form', 'screenshot', 'scroll'],
          enabled: true
        },
        {
          id: 'calendar_agent',
          name: 'Calendar Control',
          description: 'Can read and manage calendar events',
          capabilities: ['read_events', 'create_event', 'update_event', 'delete_event'],
          enabled: true
        },
        {
          id: 'file_agent',
          name: 'File Management',
          description: 'Can manage files and folders',
          capabilities: ['list_files', 'upload_file', 'download_file', 'delete_file'],
          enabled: true
        }
      ]);
    });
  }, [isLoggedIn]);

  // Check for persisted login on mount
  useEffect(() => {
    const customToken = localStorage.getItem('token') || localStorage.getItem('auth_token');
    const customUser = localStorage.getItem('user');
    
    if (customToken && customUser) {
      try {
        const parsedUser = JSON.parse(customUser);
        setUser(parsedUser);
        
        if (api && api.axios) {
          api.axios.defaults.headers['Authorization'] = `Bearer ${customToken}`;
        }
        
        setIsLoggedIn(true);
        console.log('✅ Restored login from localStorage:', parsedUser.uid);
        
        api.getMessages(parsedUser.uid, 50).then(history => {
          if (history && history.length > 0) {
            const formattedMessages = history.map(msg => ({
              id: msg.id,
              text: msg.content,
              sender: msg.isAI ? 'ai' : 'me',
              isAI: msg.isAI,
              timestamp: new Date(msg.createdAt),
              metadata: msg.metadata
            }));
            setMessages(formattedMessages);
            setMsgCount(formattedMessages.length);
            console.log('✅ Loaded chat history:', formattedMessages.length, 'messages');
          }
        }).catch(err => {
          console.warn('[App] Failed to load chat history on restore:', err.message);
        });
      } catch (e) {
        console.error('❌ Failed to restore login:', e);
      }
    }
  }, []);

  // Auth state listener - only for Firebase auth, don't override JWT auth
  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (user) => {
      // Check if we already have JWT auth (email login)
      const hasJWT = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (hasJWT) {
        console.log('JWT auth exists, skipping Firebase auth state change');
        return;
      }
      
      if (user) {
        setUser({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL
        });
        await api.initialize();
        console.log('Auth state changed - user logged in:', user.uid);
      } else {
        console.log('Auth state changed - user logged out');
      }
    });

    return () => unsubscribe();
  }, []);

  const handleStrangerChatEnded = useCallback((event) => {
      const { targetUserId, chatLog, roundCount } = event.detail;

      if (targetUserId === user?.uid) {
        const notifyMsg = {
          id: Date.now(),
          sender: 'ai',
          text: `有人与你的分身对话超过${roundCount}次，你需不需要去看看？`,
          timestamp: new Date(),
          isChatLogRequest: true,
          chatLogData: {
            strangerName: '陌生人',
            chatLog: chatLog,
            status: 'view'
          }
        };

        setMessages(prev => [...prev, notifyMsg]);
      }
    }, [user?.uid]);

    // Event listener for stranger chat
    useEffect(() => {
      window.addEventListener('strangerChatEndedWithoutFriendRequest', handleStrangerChatEnded);
      return () => window.removeEventListener('strangerChatEndedWithoutFriendRequest', handleStrangerChatEnded);
    }, [handleStrangerChatEnded]);

  // Handlers
  const handleLogin = async (loggedInUser) => {
    console.log('🔵 [App] handleLogin called with:', loggedInUser);
    
    try {
      if (loggedInUser) {
        const customToken = localStorage.getItem('token') || localStorage.getItem('auth_token');
        console.log('🔵 [App] Token exists:', !!customToken);
        
        console.log('🔵 [App] Setting user state...');
        setUser(loggedInUser);
        
        console.log('🔵 [App] Saving to localStorage...');
        localStorage.setItem('user_id', loggedInUser.uid);
        localStorage.setItem('user', JSON.stringify(loggedInUser));
        
        if (api && api.axios && customToken) {
          console.log('🔵 [App] Setting API headers...');
          api.axios.defaults.headers['Authorization'] = `Bearer ${customToken}`;
        }

        console.log('🔵 [App] Setting isLoggedIn to true...');
        setIsLoggedIn(true);
        console.log('✅ [App] Login successful:', loggedInUser.uid);
        
        setTimeout(async () => {
          try {
            console.log('🔵 [App] Auto-assigning to AI group...');
            await api.autoAssignAIGroup(loggedInUser.uid, loggedInUser.displayName || '用户');
            console.log('✅ [App] Auto-assigned to AI group');
          } catch (err) {
            console.error('❌ [App] Auto assign group failed:', err);
          }
        }, 100);
        
        return;
      }

      const firebaseUser = getCurrentUser();

      if (firebaseUser) {
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL
        });

        localStorage.setItem('user_id', firebaseUser.uid);
        localStorage.setItem('firebase_uid', firebaseUser.uid);

        await api.initialize();

        try {
          await api.autoAssignAIGroup(firebaseUser.uid, firebaseUser.displayName || '新用户');
          console.log('✅ 自动分配AI群聊成功');
        } catch (err) {
          console.error('❌ 自动分配AI群聊失败:', err);
        }

        setIsLoggedIn(true);
        console.log('Login successful with Firebase user:', firebaseUser.uid);
      } else {
        const userId = 'demo_' + Math.random().toString(36).substr(2, 9);
        setUser({ uid: userId });
        localStorage.setItem('user_id', userId);

        try {
          await api.autoAssignAIGroup(userId, '新用户');
          console.log('✅ Demo模式自动分配AI群聊成功');
        } catch (err) {
          console.error('❌ Demo模式自动分配AI群聊失败:', err);
        }

        setIsLoggedIn(true);
        console.log('Login in demo mode (no Firebase user)');
      }
    } catch (error) {
      console.error('Login failed:', error);
      const userId = 'demo_' + Math.random().toString(36).substr(2, 9);
      setUser({ uid: userId });
      localStorage.setItem('user_id', userId);
      setIsLoggedIn(true);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_email');
    setUser(null);
    setIsLoggedIn(false);
    setMessages([]);
    console.log('✅ Logged out successfully');
  };

  const handleJoinRequestResponse = async (approved) => {
    if (!groupJoinRequest) return;

    try {
      await api.approveJoinRequest(
        groupJoinRequest.groupId,
        groupJoinRequest.userId,
        approved
      );

      const responseMsg = {
        id: Date.now() + 1,
        text: approved
          ? `已同意${groupJoinRequest.userName}加入${groupJoinRequest.groupName}`
          : `已拒绝${groupJoinRequest.userName}加入${groupJoinRequest.groupName}`,
        sender: 'system',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, responseMsg]);
      setGroupJoinRequest(null);
    } catch (err) {
      console.error('处理入群申请失败:', err);
      alert('处理失败: ' + err.message);
    }
  };

  const handleGroupJoinRequest = (request) => {
    const requestMsg = {
      id: Date.now(),
      text: `${request.userName}要加入${request.groupName}`,
      sender: 'ai',
      timestamp: new Date(),
      isGroupJoinRequest: true,
      groupJoinRequest: {
        groupId: request.groupId,
        groupName: request.groupName,
        userId: request.userId,
        userName: request.userName,
        targetUserId: request.targetUserId
      }
    };

    setMessages(prev => [...prev, requestMsg]);
    setGroupJoinRequest({
      groupId: request.groupId,
      groupName: request.groupName,
      userId: request.userId,
      userName: request.userName
    });
  };

  const handleAvatarUpload = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    
    triggerHaptic();
    
    const userMsg = {
      id: Date.now(),
      text: inputValue.trim(),
      sender: 'me',
      timestamp: new Date()
    };
    setMessages(prev => {
      const newMsgs = [...prev, userMsg];
      return newMsgs.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    });
    setInputValue('');
    setMsgCount(prev => prev + 1);
    
    try {
      const savedUserMsg = await api.sendMessage(user?.uid || 'anonymous', user?.uid || 'anonymous', userName, userMsg.text, false);
      
      // 更新本地 state 为后端返回的消息（包含数据库 ID）
      setMessages(prev => prev.map(msg => 
        msg.id === userMsg.id ? { 
          ...msg, 
          id: savedUserMsg.id, 
          text: savedUserMsg.content || msg.text,
          sender: 'me', 
          timestamp: new Date(savedUserMsg.createdAt) 
        } : msg
      ));
      
      const aiResponse = await openclawService.generateReply(
        userMsg.text,
        messages,
        user?.uid || 'anonymous',
        'main_chat'
      );
      
      const aiMsg = {
        id: Date.now() + 1,
        text: aiResponse?.content !== undefined ? (aiResponse.content || '[空回复]') : 'API无响应',
        sender: 'ai',
        isAI: true,
        timestamp: new Date(),
        metadata: aiResponse?.metadata
      };
      setMessages(prev => {
        const exists = prev.some(m => m.text === aiMsg.text && m.isAI && Math.abs(new Date(m.timestamp) - aiMsg.timestamp) < 5000);
        if (exists) {
          console.log('[App] AI message already exists, skipping duplicate');
          return prev;
        }
        const newMsgs = [...prev, aiMsg];
        return newMsgs.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      });
      
      const savedAiMsg = await api.sendMessage(user?.uid || 'anonymous', user?.uid || 'anonymous', aiAssistantName, aiMsg.text, true);
      
      // 更新 AI 消息为后端返回的消息（包含数据库 ID）
      setMessages(prev => prev.map(msg => 
        msg.id === aiMsg.id ? { 
          ...msg, 
          id: savedAiMsg.id, 
          text: savedAiMsg.content || msg.text,
          sender: 'ai', 
          isAI: true, 
          timestamp: new Date(savedAiMsg.createdAt) 
        } : msg
      ));
    } catch (error) {
      console.error('OpenClaw API error:', error);
      const errorMsg = {
        id: Date.now() + 1,
        text: `API Error: ${error.message}`,
        sender: 'ai',
        isAI: true,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
    }
  };

  // Name editing handlers
  const handleStartEditingName = () => {
    setTempUserName(userName);
    setIsEditingUserName(true);
  };

  const handleSaveName = () => {
    if (tempUserName.trim()) {
      setUserName(tempUserName.trim());
      localStorage.setItem('user_display_name', tempUserName.trim());
    }
    setIsEditingUserName(false);
  };

  const handleKeyPressName = (e) => {
    if (e.key === 'Enter') {
      handleSaveName();
    }
  };

  return (
    <div className="w-full h-[100dvh] max-w-md mx-auto bg-[#050510] overflow-hidden fixed inset-0 sm:relative sm:inset-auto shadow-2xl sm:border-x border-white/10">
      <GlobalStyles />
      <StatusBar />

      {!isLoggedIn ? (
        <LoginScreen onLogin={handleLogin} t={t} />
      ) : (
        <>
          {/* Chat Interface */}
          <div className={`h-full flex flex-col relative z-10 bg-transparent transition-all duration-300 ${isSidebarOpen ? 'blur-sm scale-95' : ''}`}>
             <ChatHeader
               isEditingUserName={isEditingUserName}
               tempUserName={tempUserName}
               userName={userName}
              t={t}
              aiAssistantName={aiAssistantName}
              onStartEditingName={handleStartEditingName}
              onSaveName={handleSaveName}
              onNameChange={setTempUserName}
              onKeyPressName={handleKeyPressName}
               onToggleSidebar={() => setIsSidebarOpen(true)}

               onToggleGroups={() => setIsGroupsOpen(true)}
                onToggleSubscription={() => setIsSubscriptionOpen(true)}
               />

              <MessageList
              messages={messages}
              avatarImage={avatarImage}
              aiAssistantName={aiAssistantName}
              userName={userName}
              groupJoinRequest={groupJoinRequest}
              friendRequest={friendRequest}
              onAcceptJoin={() => handleJoinRequestResponse(true)}
              onRejectJoin={() => handleJoinRequestResponse(false)}
              onViewChatLog={() => {
                if (friendRequest) {
                  setChatLogSheet({
                    isOpen: true,
                    chatLog: friendRequest.chatLog || [],
                    strangerName: friendRequest.strangerName,
                    status: 'pending'
                  });
                } else if (messages.find(m => m.isChatLogRequest)?.chatLogData) {
                  const msg = messages.find(m => m.isChatLogRequest);
                  setChatLogSheet({
                    isOpen: true,
                    chatLog: msg.chatLogData.chatLog,
                    strangerName: msg.chatLogData.strangerName,
                    status: 'viewed'
                  });
                }
              }}
              onIgnoreFriend={(requestData) => {
                if (requestData) {
                  const strangerName = requestData.strangerName || requestData.strangerId || '用户';
                  const ignoreMsg = {
                    id: Date.now(),
                    sender: 'system',
                    text: `已忽略${strangerName}的好友申请`,
                    timestamp: new Date()
                  };
                  setMessages(prev => [...prev, ignoreMsg]);
                  if (requestData.id) {
                    api.markNotificationsRead(user?.uid, requestData.id);
                  }
                }
              }}
              onAcceptFriend={async (requestData) => {
                if (requestData) {
                  const strangerId = requestData.strangerId;
                  const strangerName = requestData.strangerName || strangerId || '用户';
                  
                  try {
                    await api.acceptFriend(user?.uid, strangerId);
                    const acceptMsg = {
                      id: Date.now(),
                      sender: 'system',
                      text: `已通过${strangerName}的好友申请`,
                      timestamp: new Date()
                    };
                    setMessages(prev => [...prev, acceptMsg]);
                    if (requestData.id) {
                      api.markNotificationsRead(user?.uid, requestData.id);
                    }
                  } catch (err) {
                    console.error('Accept friend failed:', err);
                    alert('接受好友失败: ' + err.message);
                  }
                }
              }}
              onIgnoreChatLog={() => {
                const msg = messages.find(m => m.isChatLogRequest);
                if (msg) {
                  const ignoreMsg = {
                    id: Date.now(),
                    sender: 'system',
                    text: '已忽略',
                    timestamp: new Date()
                  };
                  setMessages(prev => [...prev.filter(m => m.id !== msg.id), ignoreMsg]);
                }
              }}
               t={t}
               userId={user?.uid}
               onMessageDeleted={(msgId) => {
                 setMessages(prev => prev.filter(m => m.id !== msgId));
               }}
               chatType="private"
               />

              <MessageInput
              inputValue={inputValue}
              onInputChange={setInputValue}
              onSubmit={handleSendMessage}
              t={t}
              onOpenMoments={() => setIsMomentsOpen(true)}
              onOpenChat={() => setIsChatListOpen(true)}
                onSendVoice={async (blob, duration) => {
                 const reader = new FileReader();
                 reader.readAsDataURL(blob);
                 reader.onload = () => {
                   const voiceMsg = {
                     id: Date.now(),
                     text: reader.result,
                     sender: 'me',
                     timestamp: new Date(),
                     voiceDuration: duration,
                     voiceData: reader.result
                   };
                   setMessages(prev => [...prev, voiceMsg]);
                   triggerHaptic();

                   // Send to OpenClaw backend
                   openclawService.sendMediaMessage(reader.result, 'voice', '', duration);
                 };
               }}
               onSendImage={async (imageData) => {
                 const imgMsg = {
                   id: Date.now(),
                   text: imageData,
                   sender: 'me',
                   timestamp: new Date(),
                   isImage: true,
                   imageData: imageData
                 };
                 setMessages(prev => [...prev, imgMsg]);
                 triggerHaptic();

                 // Send to OpenClaw backend
                 openclawService.sendMediaMessage(imageData, 'image', '');
               }}
            />
          </div>

           {/* Sidebar Menu */}
            <SidebarMenu
              isOpen={isSidebarOpen}
              onClose={() => setIsSidebarOpen(false)}
              userId={user?.uid}
              settings={settings}
              onUpdateSettings={setSettings}
              t={t}
              avatarImage={avatarImage}
              onAvatarUpload={handleAvatarUpload}
              userName={userName}
              aiAssistantName={aiAssistantName}
              onAiNameChange={setAiAssistantName}
              agentConfig={agentConfig}
              setAgentConfig={setAgentConfig}
              availableAgents={availableAgents}
              onLogout={handleLogout}
              onScanUser={(data) => {
                setIsSidebarOpen(false);
                setStrangerChatData({
                  targetUserId: data.targetUserId,
                  targetUserName: data.targetUserName,
                  targetAvatar: data.targetAvatar
                });
                setShowStrangerChat(true);
              }}
              onChatWithFriend={(friend) => {
                setIsSidebarOpen(false);
                setActivePrivateChat(friend);
              }}
              onShowAvatarCard={() => {
                setIsSidebarOpen(false);
                setShowQRCode(true);
              }}
            />

          {/* QR Code Modal */}
          <QRCodeModal
            isOpen={showQRCode}
            onClose={() => setShowQRCode(false)}
            t={t}
            avatarImage={avatarImage}
            userName={userName}
            userId={user?.uid}
            onSimulateStranger={() => {
              setShowQRCode(false);
              setStrangerChatData({
                targetUserId: user?.uid,
                targetUserName: userName,
                targetAvatar: avatarImage
              });
              setShowStrangerChat(true);
            }}
          />

          {/* Stranger Chat Modal */}
          {showStrangerChat && strangerChatData && (
            <StrangerChatModal
              isOpen={showStrangerChat}
              onClose={() => {
                setShowStrangerChat(false);
                setStrangerChatData(null);
              }}
              targetUserId={strangerChatData.targetUserId}
              targetUserName={strangerChatData.targetUserName}
              targetAvatar={strangerChatData.targetAvatar}
              t={t}
              onAddFriend={async (targetId, targetName) => {
                try {
                  await api.addFriend(user?.uid, targetId);
                  
                  const requestMsg = {
                    id: Date.now(),
                    sender: 'system',
                    text: `已向${targetName}发送好友申请，等待对方确认`,
                    timestamp: new Date(),
                    isFriendRequest: true,
                    requestData: {
                      strangerId: targetId,
                      strangerName: targetName,
                      chatLog: [],
                      status: 'pending'
                    }
                  };
                  setMessages(prev => [...prev, requestMsg]);
                  setFriendRequest({
                    type: 'to_stranger',
                    strangerId: targetId,
                    strangerName: targetName,
                    chatLog: [],
                    status: 'pending'
                  });
                  
                  alert(`已向${targetName}发送好友申请`);
                } catch (err) {
                  console.error('发送好友请求失败:', err);
                  alert('发送好友请求失败: ' + (err.message || '请稍后再试'));
                }
              }}
            />
          )}

          {/* Chat Log Sheet */}
          <ChatLogSheet
            isOpen={chatLogSheet.isOpen}
            onClose={() => setChatLogSheet(prev => ({ ...prev, isOpen: false }))}
            chatLog={chatLogSheet.chatLog}
            strangerName={chatLogSheet.strangerName}
            status={chatLogSheet.status}
            onAddFriend={() => {
              if (friendRequest) {
                const acceptMsg = {
                  id: Date.now(),
                  sender: 'system',
                  text: `已通过${friendRequest.strangerName}的好友申请`,
                  timestamp: new Date()
                };
                setMessages(prev => [...prev, acceptMsg]);
                setFriendRequest(null);
                setChatLogSheet(prev => ({ ...prev, isOpen: false, status: 'accepted' }));
              }
            }}
            onIgnore={() => {
              if (friendRequest) {
                const ignoreMsg = {
                  id: Date.now(),
                  sender: 'system',
                  text: `已忽略${friendRequest.strangerName}的好友申请`,
                  timestamp: new Date()
                };
                setMessages(prev => [...prev, ignoreMsg]);
                setFriendRequest(null);
                setChatLogSheet(prev => ({ ...prev, isOpen: false, status: 'ignored' }));
              }
            }}
            onPass={() => {
              setChatLogSheet(prev => ({ ...prev, isOpen: false }));
            }}
          />

          {/* Group List Modal */}
          <GroupListModal
            isOpen={isGroupsOpen}
            onClose={() => setIsGroupsOpen(false)}
            userId={user?.uid}
            userName={userName}
            t={t}
            onRequestJoin={handleGroupJoinRequest}
            onOpenUserGroup={(group) => {
              setActiveUserGroup(group);
            }}
          />

          <Moments
            isOpen={isMomentsOpen}
            onClose={() => setIsMomentsOpen(false)}
            userId={user?.uid}
            userName={userName}
            userAvatar={avatarImage}
          />

          <ChatList
            isOpen={isChatListOpen}
            onClose={() => setIsChatListOpen(false)}
            userId={user?.uid}
            onOpenPrivateChat={(friend) => setActivePrivateChat(friend)}
            onOpenGroupChat={(groupId, groupName) => {
              setActiveUserGroup({ id: groupId, name: groupName });
            }}
          />

          {/* User Group Chat */}
          {activeUserGroup && (
            <UserGroupChat
              group={activeUserGroup}
              onClose={() => setActiveUserGroup(null)}
              userId={user?.uid}
              userName={userName}
              userAvatar={avatarImage}
              onUpdateGroupName={(groupId, newName) => {
                setActiveUserGroup(prev => ({ ...prev, name: newName }));
              }}
              onAddFriend={async (targetId, targetName) => {
                try {
                  await api.addFriend(user?.uid, targetId);
                  alert(`已向${targetName}发送好友申请`);
                } catch (err) {
                  console.error('发送好友请求失败:', err);
                  alert('发送好友请求失败: ' + (err.message || '请稍后再试'));
                }
              }}
            />
          )}

          {activePrivateChat && (
            <PrivateChat
              friend={activePrivateChat}
              userId={user?.uid}
              userName={userName}
              userAvatar={avatarImage}
              onClose={() => setActivePrivateChat(null)}
              onDeleteFriend={async (friendId) => {
                try {
                  await api.deleteFriend(user?.uid, friendId);
                  setActivePrivateChat(null);
                } catch (err) {
                  alert('删除好友失败: ' + err.message);
                }
              }}
              onCreateGroup={() => {
                loadFriends();
                setShowFriendPicker(true);
              }}
            />
          )}

          <FriendPickerModal
            isOpen={showFriendPicker}
            onClose={() => setShowFriendPicker(false)}
            userId={user?.uid}
            friends={friends}
            title="选择好友发起群聊"
            onConfirm={async (selectedFriends) => {
              if (selectedFriends.length < 1) return;
              try {
                const friendIds = selectedFriends.map(f => f.friendId);
                const group = await api.createGroup(
                  `群聊`,
                  user?.uid,
                  friendIds
                );
                console.log('群聊创建成功:', group);
                setActivePrivateChat(null);
                if (group) {
                  setActiveUserGroup(group);
                }
              } catch (err) {
                console.error('创建群聊失败:', err);
                alert('创建群聊失败: ' + err.message);
              }
            }}
          />

          {/* Subscription Modal */}
          <SubscriptionModal
            isOpen={isSubscriptionOpen}
            onClose={() => setIsSubscriptionOpen(false)}
            t={t}
            msgCount={msgCount}
            language={settings.language}
            userId={user?.uid}
          />
        </>
      )}
    </div>
  );
};

export default App;
